import {StageProvider} from '@pinecast/common/Stage';
import {GQLProvider} from '@pinecast/gql';
import {Provider as I18nProvider} from '@pinecast/i18n';
import {DarkModeProvider} from '@pinecast/styles';
import {Provider as StyletronProvider} from '@pinecast/styles/provider';
import * as React from 'react';
import {createRoot} from 'react-dom/client';

import type {Mountable} from './types';

/**
 * The node to render into. The dashboard's page shell used to mount the app
 * in `<main class="omnibus">`, which put the header and the sidebar inside
 * the main landmark. The app renders its own `<main>` now (`Chrome`), so a
 * `MAIN` node becomes a `DIV` with the same attributes and content first.
 * This works with the page shell of either version; prompt D2 changes the
 * shell itself.
 */
export function mountNode(elem: HTMLElement): HTMLElement {
  if (elem.tagName !== 'MAIN') {
    return elem;
  }
  const div = document.createElement('div');
  for (const {name, value} of Array.from(elem.attributes)) {
    div.setAttribute(name, value);
  }
  div.append(...Array.from(elem.childNodes));
  elem.replaceWith(div);
  return div;
}

export default function mount(node: React.ReactNode, elem: HTMLElement) {
  const root = createRoot(mountNode(elem));
  root.render(
    <React.StrictMode>
      <I18nProvider>
        <StyletronProvider>
          <DarkModeProvider>
            <GQLProvider>
              <StageProvider>{node}</StageProvider>
            </GQLProvider>
          </DarkModeProvider>
        </StyletronProvider>
      </I18nProvider>
    </React.StrictMode>,
  );
}

export function applyProps(
  component: Mountable<Record<string, any>>,
  elem: HTMLElement,
) {
  return React.createElement(
    component,
    Object.keys(component.propExtraction).reduce(
      (acc, cur) => {
        acc[cur] = component.propExtraction[cur](elem);
        return acc;
      },
      {} as {[key: string]: any},
    ),
  );
}
